// 작업지시서 백엔드 = china-inapi (POST/GET /china/dental-lab/work-orders).
// 기존 한국 inapi 경로(/dental-laboratory/work-orders)에서 전환.
// $apiInstance 는 WorkOrderService 에서 chinaInapi 인스턴스를 주입한다(JWT Bearer 자동부착).
export default function($apiInstance, $store) {
    return {
        /**
         * 작업 지시서 생성 (보철 인증서 정보 전달)
         * @param {Object} data - 작업 지시서 데이터
         * @param {Function} success - 성공 콜백
         * @param {Function} fail - 실패 콜백
         */
        createWorkOrder: async (data, success, fail) => {
            $store.commit('setStateValue', { loading: true });
            try {
                const response = await $apiInstance.post('/china/dental-lab/work-orders', data);

                if (response.status === 200 || response.status === 201) {
                    if (success) success(response);
                } else {
                    if (fail) fail(response);
                }
            } catch (error) {
                console.error('작업 지시서 생성 실패:', error);
                if (fail) fail(error);
            } finally {
                $store.commit('setStateValue', { loading: false });
            }
        },

        /**
         * 작업 지시서 목록 조회 (수기 작업지시서 리스트 화면, SPEC-CHINA-LAB-MANUAL-WORKORDER-LIST-001)
         * GET /china/dental-lab/work-orders
         * 허용된 필드만 전달 (화이트리스트, [[feedback_fe_api_wrapper_param_whitelist]]) —
         * china-inapi ListWorkOrdersQueryDto와 1:1 대응.
         * @param {Object} params
         *   - startDate, endDate (YYYY-MM-DD) — 작성일(createdAt) 범위
         *   - patientMobile - 환자 휴대폰 (BE가 '-' 제거 후 매칭)
         *   - patientName   - 환자명 부분검색
         *   - randomNo      - 난수 부분검색 ("No." 접두어는 BE가 자동 제거)
         *   - gtinCode      - GTIN 부분검색
         *   - status        - 상태 필터 ('pending'|'completed', SPEC-CHINA-DEALER-DASHBOARD-001 REQ-BE-008/REQ-FE-004)
         *   - dentalLaboratoryId - 기공소 ID (ObjectId 문자열, 무효값은 BE 400 INVALID_DENTAL_LABORATORY_ID)
         *   - hospitalId    - 병원 ID (hospital.hospitalId 대조)
         *   - page, limit   - 페이지네이션
         * @returns {Promise} axios response — response.data: { success, total, page, totalPages, data }
         */
        getWorkOrders: (params = {}) => {
            const query = {};
            if (params.startDate) query.startDate = params.startDate;
            if (params.endDate) query.endDate = params.endDate;
            if (params.patientMobile) query.patientMobile = params.patientMobile;
            if (params.patientName) query.patientName = params.patientName;
            if (params.randomNo) query.randomNo = params.randomNo;
            if (params.gtinCode) query.gtinCode = params.gtinCode;
            // status는 'pending'|'completed'만 허용 — 무효값은 BE 400 방지를 위해 무시
            if (params.status === 'pending' || params.status === 'completed') query.status = params.status;
            if (params.dentalLaboratoryId) query.dentalLaboratoryId = params.dentalLaboratoryId;
            if (params.hospitalId) query.hospitalId = params.hospitalId;
            if (params.page) query.page = params.page;
            if (params.limit) query.limit = params.limit;
            return $apiInstance.get('/china/dental-lab/work-orders', { params: query });
        },

        /**
         * 작업 지시서 상세 조회
         * @param {String} id - 작업 지시서 ID
         * @param {Function} success - 성공 콜백
         * @param {Function} fail - 실패 콜백
         */
        getWorkOrderDetail: async (id, success, fail) => {
            $store.commit('setStateValue', { loading: true });
            try {
                const response = await $apiInstance.get(`/china/dental-lab/work-orders/${id}`);

                if (response.status === 200) {
                    if (success) success(response);
                } else {
                    if (fail) fail(response);
                }
            } catch (error) {
                console.error('작업 지시서 상세 조회 실패:', error);
                if (fail) fail(error);
            } finally {
                $store.commit('setStateValue', { loading: false });
            }
        },

        /**
         * 발행여부 게이트 조회 (SPEC-CHINA-ADMIN-WORKORDER-EDIT-RELEASE-001 REQ-FE-002)
         * GET /china/dental-lab/work-orders/:id/issuance-status  (P0-FIX 2026-07-21)
         * 화면 주 사용자는 기공소(DENTAL_LABORATORY)인데 M4 EP(/products/admin/dentalmax/china/...)는
         * TenantGuard로 제조사 테넌트만 통과시켜 기공소 JWT가 403이 된다. 그래서 role 기반 래퍼 EP를
         * 소비한다 — BE가 소유권 스코핑 후 동일 UNION usecase를 dentalmax 테넌트로 호출한다(REQ-STOP-001 준수).
         * @param {String} workOrderId
         * @returns {Promise} axios response — response.data: { success, data: { workOrderId, issued, cards: [{ randomNo, selfIssued, issued }] } }
         */
        getIssuanceStatus: (workOrderId) => {
            return $apiInstance.get(`/china/dental-lab/work-orders/${workOrderId}/issuance-status`);
        },

        /**
         * 작업지시서 행위 감사 목록 (证书回收·卡片解绑 행위자 이력)
         * GET /china/work-order-action-audits?workOrderId=
         * BE 사이드카(work-order-action-audit) — 스코핑은 서버 책임(운영자=전체, 그 외=소유 기공소/행위자).
         * @param {String} workOrderId
         * @returns {Promise} axios response — response.data: { items: [{ action, randomNo, actor, createdAt, result }] }
         */
        getWorkOrderAudits: (workOrderId) => {
            return $apiInstance.get('/china/work-order-action-audits', { params: { workOrderId } });
        },

        /**
         * 최종 수정자 일괄 조회 (목록 화면 "최종 수정자" 컬럼용, N+1 방지)
         * GET /china/work-order-action-audits/latest-edit?workOrderIds=<comma-separated 24-hex ids, max 100>
         * @param {string[]} workOrderIds - 24자리 hex id 배열 (최대 100개, 초과 시 BE가 400 반환하므로 호출측이 청크 분할)
         * @returns {Promise} axios response — response.data: { success, data: { [workOrderId]: { actor: { username, accountId, role }, createdAt } } }
         *   기록이 없는 작업지시서 id는 키 자체가 응답에서 생략된다.
         */
        getLatestEditAudits: (workOrderIds = []) => {
            const ids = (workOrderIds || []).filter(Boolean);
            if (ids.length === 0) return Promise.resolve({ data: { success: true, data: {} } });
            return $apiInstance.get('/china/work-order-action-audits/latest-edit', {
                params: { workOrderIds: ids.join(',') },
            });
        },

        /**
         * 최근 행위 감사 전체 목록 — 운영자(XINLAIAN) 전용 EP.
         * GET /china/work-order-action-audits/recent
         * 전량 해제(soft-delete)로 목록에서 사라진 작업지시서의 기록 포함(workOrder 라벨 조인).
         * @param {Number} limit
         * @returns {Promise} axios response — response.data: { items: [{ ..., workOrder: { patientName, labName, removed } }] }
         */
        getRecentWorkOrderAudits: (limit = 50) => {
            return $apiInstance.get('/china/work-order-action-audits/recent', { params: { limit } });
        },

        /**
         * soft-delete 된 작업지시서 목록 — 운영자(XINLAIAN) 전용 EP, 읽기 전용 원문.
         * GET /china/work-order-action-audits/removed-work-orders
         * "삭제된 작업지시서 보기" 토글이 소비한다.
         * @param {Number} limit
         * @returns {Promise} axios response — response.data: { items: [작업지시서 원문] }
         */
        getRemovedWorkOrders: (limit = 100) => {
            return $apiInstance.get('/china/work-order-action-audits/removed-work-orders', { params: { limit } });
        },

        /**
         * 작업지시서 수정 (REQ-EDIT-002)
         * PATCH /china/dental-lab/work-orders/:id
         * 화이트리스트 준수: 환자메타/hospital/날짜/chartNumber/doctor/prosthetic.toothDetails(카드구성)만 전달.
         * randomNo/gtin/lot 등 정품근거는 BE가 무시하므로 애초에 payload에 포함하지 않는다.
         * @param {String} id - 작업 지시서 ID
         * @param {Object} data - 화이트리스트 payload
         * @returns {Promise} axios response
         */
        updateWorkOrder: (id, data = {}) => {
            const payload = {};
            if (data.patient) payload.patient = data.patient;
            if (data.hospital) payload.hospital = data.hospital;
            if (data.chartNumber !== undefined) payload.chartNumber = data.chartNumber;
            if (data.doctor !== undefined) payload.doctor = data.doctor;
            if (data.prostheticDeliveryDate !== undefined) payload.prostheticDeliveryDate = data.prostheticDeliveryDate;
            if (data.prosthetic) payload.prosthetic = data.prosthetic;
            return $apiInstance.patch(`/china/dental-lab/work-orders/${id}`, payload);
        },

        /**
         * 인증서회수(证书回收) — 발행된 작업지시서의 전 카드를 orchestrated 회수.
         * POST /china/dental-lab/work-orders/:id/recover
         * 개별 randomNo 단위 recover-certificate EP를 여기서 직접 소비하지 않는다 — 본 SPEC 소유 도메인의
         * 오케스트레이션 EP(REQ-ORCH-001)를 경유해 recover→release 보상 흐름을 서버가 관장한다.
         * @param {String} id - 작업 지시서 ID
         * @returns {Promise} axios response
         */
        recoverWorkOrder: (id) => {
            return $apiInstance.post(`/china/dental-lab/work-orders/${id}/recover`);
        },

        /**
         * 카드 배정해제(卡片解绑) — 작업지시서 단위 전체 해제.
         * POST /china/dental-lab/work-orders/:id/release
         * @param {String} id - 작업 지시서 ID
         * @returns {Promise} axios response — response.data: { releasedCount }
         */
        releaseWorkOrderCards: (id) => {
            return $apiInstance.post(`/china/dental-lab/work-orders/${id}/release`);
        },

        /**
         * 카드 배정해제(卡片解绑) — 개별 카드 단위 해제.
         * POST /china/dental-lab/work-orders/cards/:randomNo/release
         * @param {String} randomNo
         * @returns {Promise} axios response — response.data: { released }
         */
        releaseCard: (randomNo) => {
            return $apiInstance.post(`/china/dental-lab/work-orders/cards/${randomNo}/release`);
        }
    };
}